import {useMemo} from 'react';

import {DEFAULT_COL_WIDTH} from '../constants/defaultColWidth';
import {SELECTION_COLUMN_WIDTH} from '../constants/selectionColumn';
import {useColumnStates} from '../store/selectors';
import {useOrderedVisibleColumns} from '../store/selectors/useOrderedVisibleColumns';
import {usePinnedLeftColumns} from '../store/selectors/usePinnedLeftColumns';
import {usePinnedRightColumns} from '../store/selectors/usePinnedRightColumns';
import {useVisibleColumns} from '../store/selectors/useVisibleColumns';
import type {EagColumn} from '../types/EagColumn';
import type {EagColumnState} from '../types/EagColumnState';
import type {EagGridTableProps} from '../types/EagGridTableProps';
import {computeColIndexMap} from '../utils/computeColIndexMap';
import {flattenColumnConfig} from '../utils/flattenColumnConfig';

// Projects an ordered list of column states into their EagColumn definitions,
// dropping any state whose key has no matching column in flatColumnMap.
// Module-level so it is a stable, non-reactive reference (keeps the useMemo
// dependency arrays as [flatColumnMap, …states] for the React Compiler).
const toColumns = (flatColumnMap: Map<string, EagColumn>, states: EagColumnState[]): EagColumn[] =>
  states
    .map((cs) => flatColumnMap.get(cs.key))
    .filter((col): col is EagColumn => col !== undefined);

// Accumulates sticky CSS offsets along a run of pinned columns. `seed` is the first
// column's offset (the leading checkbox/action column width); each subsequent column
// is offset by the running total of preceding column widths. Module-level so it stays a
// stable, non-reactive reference for the React Compiler.
const computeStickyOffsets = (columns: EagColumnState[], seed: number): Map<string, number> => {
  const offsets = new Map<string, number>();
  let accum = seed;
  for (const cs of columns) {
    offsets.set(cs.key, accum);
    accum += cs.width ?? DEFAULT_COL_WIDTH;
  }
  return offsets;
};

type UseColumnLayoutParams = {
  columnConfig: EagGridTableProps['columnConfig'];
  hasCheckboxColumn: boolean;
  actionColumnWidth: number;
};

type UseColumnLayoutReturn = {
  // Raw column state subscriptions
  columnStates: EagColumnState[];
  visibleColumnStates: EagColumnState[];
  orderedVisibleColumnStates: EagColumnState[];
  // Column-config-derived collections
  flatColumns: EagColumn[];
  flatColumnMap: Map<string, EagColumn>;
  visibleColumns: EagColumn[];
  // EagColumn views of the …ColumnStates partitions below, for cell rendering.
  nonRightPinnedColumns: EagColumn[];
  rightPinnedColumns: EagColumn[];
  visibleColumnIndexMap: Map<string, number>;
  visibleColumnMap: Map<string, EagColumn>;
  visibleColumnStateMap: Map<string, EagColumnState>;
  columnStateMap: Map<string, EagColumnState>;
  // Width geometry
  columnWidthMap: Map<string, number>;
  getWidth: (key: string) => number;
  colIndexMap: Map<string, number>;
  // Pinning groups + sticky offsets
  nonRightPinnedColumnStates: EagColumnState[];
  rightPinnedColumnStates: EagColumnState[];
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
};

/**
 * Derives all column-layout geometry from store state + grid-level inputs.
 *
 * Encapsulates the pure-derivation half of EagGridTable: flattening the config,
 * computing sticky offsets, building O(1) lookup Maps, partitioning visible
 * columns by pin position, and producing the sort model.
 *
 * The hook subscribes to the store via existing selector hooks. Callers pass
 * the non-store inputs (`columnConfig`, `hasCheckboxColumn`, `actionColumnWidth`).
 * The result is a flat record of derived values ready for direct consumption
 * by JSX, child hooks (`useColumnResize`, `useGridDragAndDrop`),
 * and the data lifecycle's sort trigger (`sortModelKey`).
 *
 * `actionColumnWidth` must be supplied by `useGridActionColumn` because
 * `pinnedRightOffsets` accumulates from the action column's left edge inward.
 */
export const useColumnLayout = (params: UseColumnLayoutParams): UseColumnLayoutReturn => {
  const columnStates = useColumnStates();
  const visibleColumnStates = useVisibleColumns();
  const orderedVisibleColumnStates = useOrderedVisibleColumns();
  const pinnedLeftColumns = usePinnedLeftColumns();
  const pinnedRightColumns = usePinnedRightColumns();

  const nonRightPinnedColumnStates = useMemo(
    () => orderedVisibleColumnStates.filter((cs) => cs.pinned !== 'RIGHT'),
    [orderedVisibleColumnStates]
  );
  const rightPinnedColumnStates = useMemo(
    () => orderedVisibleColumnStates.filter((cs) => cs.pinned === 'RIGHT'),
    [orderedVisibleColumnStates]
  );

  /**
   * @mandatory Maps column keys to their <col> DOM index in the colgroup for resize.
   * Right-pinned columns shift by +1 due to the filler <col> between unpinned and right-pinned.
   * hasCheckboxColumn adds +1 to all indices for the checkbox <col> at position 0.
   * Must be referentially stable: used as a prop passed to HeaderRow.colIndexMap.
   */
  const colIndexMap = useMemo(
    () =>
      computeColIndexMap(
        orderedVisibleColumnStates,
        nonRightPinnedColumnStates.length,
        params.hasCheckboxColumn
      ),
    [orderedVisibleColumnStates, nonRightPinnedColumnStates.length, params.hasCheckboxColumn]
  );

  // columnConfig is stable after mount (dynamic changes require remounting via key prop),
  // so this memo runs only once in practice.
  const flatColumns = useMemo(
    () => flattenColumnConfig(params.columnConfig),
    [params.columnConfig]
  );

  /**
   * @mandatory O(1) width lookup Map used by getWidth() inside useColumnResize.
   * new Map() from array data is not guaranteed referentially stable by React Compiler.
   */
  const columnWidthMap = useMemo(
    () => new Map(columnStates.map((cs) => [cs.key, cs.width ?? DEFAULT_COL_WIDTH])),
    [columnStates]
  );

  const getWidth = (key: string): number => columnWidthMap.get(key) ?? DEFAULT_COL_WIDTH;

  /**
   * @mandatory Reference-stable Map of sticky left CSS offsets for pinned-left columns.
   * Passed as prop to HeaderRow and GridBody which render it into N*M cells.
   * Unstable reference would cause all rows to re-render on every unrelated state change.
   */
  const pinnedLeftOffsets = useMemo(
    () =>
      computeStickyOffsets(
        pinnedLeftColumns,
        params.hasCheckboxColumn ? SELECTION_COLUMN_WIDTH : 0
      ),
    [pinnedLeftColumns, params.hasCheckboxColumn]
  );

  /**
   * @mandatory Reference-stable Map of sticky right CSS offsets for pinned-right columns.
   * Same rationale as pinnedLeftOffsets. Starts from actionColumnWidth so right-pinned
   * columns sit to the left of the fixed action column (sticky at right: 0).
   */
  const pinnedRightOffsets = useMemo(
    () => computeStickyOffsets([...pinnedRightColumns].reverse(), params.actionColumnWidth),
    [pinnedRightColumns, params.actionColumnWidth]
  );

  /**
   * @mandatory O(1) column state Map passed into columnMenuContextValue.
   * Must be referentially stable — it is a dep of the columnMenuContextValue useMemo,
   * so an unstable Map would cause the context value to rebuild on every render.
   */
  const columnStateMap = useMemo(
    () => new Map(columnStates.map((cs) => [cs.key, cs])),
    [columnStates]
  );

  /**
   * @mandatory O(1) lookup Map of EagColumn by key, passed to useColumnAutosize.
   * new Map() from array is not guaranteed referentially stable by React Compiler.
   */
  const flatColumnMap = useMemo(
    () => new Map(flatColumns.map((col) => [col.key, col])),
    [flatColumns]
  );

  // visibleColumns is derived from orderedVisibleColumnStates so that the rendered
  // column DOM order is always [pinnedLeft | unpinned | pinnedRight] — required for
  // CSS `position: sticky` with left/right offsets to work correctly on table cells.
  // DnD operations use visibleColumnStates (store order) separately.
  const visibleColumns = useMemo(
    () => toColumns(flatColumnMap, orderedVisibleColumnStates),
    [flatColumnMap, orderedVisibleColumnStates]
  );

  /**
   * @mandatory Visible EagColumns partitioned by right-pin, in render order.
   * Shared by HeaderRow and every GridRow — previously each recomputed this
   * partition per render (GridRow: per row). Must be referentially stable
   * because GridRow is memoized.
   */
  const nonRightPinnedColumns = useMemo(
    () => toColumns(flatColumnMap, nonRightPinnedColumnStates),
    [flatColumnMap, nonRightPinnedColumnStates]
  );
  const rightPinnedColumns = useMemo(
    () => toColumns(flatColumnMap, rightPinnedColumnStates),
    [flatColumnMap, rightPinnedColumnStates]
  );
  /**
   * @mandatory Map of column key → index within visibleColumns (render order).
   * Used by GridRow to detect the last data column; replaces the per-row
   * globalIndexMap rebuild. Must be referentially stable (GridRow is memoized).
   */
  const visibleColumnIndexMap = useMemo(
    () => new Map(visibleColumns.map((col, i) => [col.key, i])),
    [visibleColumns]
  );

  /**
   * @mandatory O(1) lookup Maps for DragOverlay column resolution.
   * new Map() from array is not guaranteed referentially stable by React Compiler.
   */
  const visibleColumnMap = useMemo(
    () => new Map(visibleColumns.map((col) => [col.key, col])),
    [visibleColumns]
  );
  const visibleColumnStateMap = useMemo(
    () => new Map(orderedVisibleColumnStates.map((cs) => [cs.key, cs])),
    [orderedVisibleColumnStates]
  );

  // Pinning edge keys: last left-pinned column key and first right-pinned column key.
  // Used to add drop shadows on the boundary between pinned and non-pinned cells.
  // For an empty array the optional chain + nullish coalesce already yields null, so no
  // explicit length guard is needed.
  const lastPinnedLeftKey = pinnedLeftColumns.at(-1)?.key ?? null;
  const firstPinnedRightKey = pinnedRightColumns[0]?.key ?? null;

  return {
    columnStates,
    visibleColumnStates,
    orderedVisibleColumnStates,
    flatColumns,
    flatColumnMap,
    visibleColumns,
    nonRightPinnedColumns,
    rightPinnedColumns,
    visibleColumnIndexMap,
    visibleColumnMap,
    visibleColumnStateMap,
    columnStateMap,
    columnWidthMap,
    getWidth,
    colIndexMap,
    nonRightPinnedColumnStates,
    rightPinnedColumnStates,
    pinnedLeftOffsets,
    pinnedRightOffsets,
    lastPinnedLeftKey,
    firstPinnedRightKey,
  };
};
